import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceOperatorService, { OperatorReview } from '../../../services/marketplaceOperatorService';
import { extractErrorMessage } from '../../../services/marketplaceService';
import StarRating from '../Reviews/StarRating';

type Filter = 'all' | 'approved' | 'hidden';
const FILTERS: Filter[] = ['all', 'approved', 'hidden'];

/** Bewertungen ohne Vormoderation; der Operator kann einzelne Beiträge aus- und wieder einblenden (E13). */
const OperatorReviewsPanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [filter, setFilter] = useState<Filter>('all');
    const [reviews, setReviews] = useState<OperatorReview[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setReviews(await marketplaceOperatorService.getReviews(filter === 'all' ? undefined : filter));
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceOperator.common.loadError')));
        } finally {
            setLoading(false);
        }
    }, [filter, translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const toggle = async (review: OperatorReview) => {
        setBusyId(review.id);
        try {
            await marketplaceOperatorService.setReviewHidden(review.id, review.status === 'approved');
            notify(translate('marketplaceOperator.reviews.updated'), { type: 'success' });
            await load();
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceOperator.common.actionError')), { type: 'error' });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>{translate('marketplaceOperator.reviews.title')}</Typography>
            <Typography variant="body2" sx={{ color: 'var(--oa-text-2)', mb: 2, maxWidth: 760 }}>{translate('marketplaceOperator.reviews.intro')}</Typography>
            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)} sx={{ mb: 2 }} aria-label={translate('marketplaceOperator.reviews.title')}>
                {FILTERS.map((f) => <ToggleButton key={f} value={f}>{translate(`marketplaceOperator.reviews.filter.${f}`)}</ToggleButton>)}
            </ToggleButtonGroup>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Skeleton variant="rectangular" height={140} />
            ) : reviews.length === 0 && !error ? (
                <Alert severity="info">{translate('marketplaceOperator.reviews.empty')}</Alert>
            ) : (
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                    {reviews.map((review) => (
                        <Paper key={review.id} variant="outlined" sx={{ p: 2 }} data-testid={`op-review-${review.id}`}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <StarRating value={review.rating} size={18} />
                                    <Typography variant="subtitle2">{review.listingTitle}</Typography>
                                    <Chip size="small" variant="outlined" label={translate(review.status === 'hidden' ? 'marketplaceOperator.reviews.hidden' : 'marketplaceOperator.reviews.visible')} />
                                </Box>
                                <Button size="small" variant="outlined" disabled={busyId === review.id} onClick={() => toggle(review)}>
                                    {translate(review.status === 'hidden' ? 'marketplaceOperator.reviews.unhide' : 'marketplaceOperator.reviews.hide')}
                                </Button>
                            </Box>
                            {review.title && <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>{review.title}</Typography>}
                            {review.body && <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{review.body}</Typography>}
                            {review.sellerReply && (
                                <Typography variant="caption" display="block" sx={{ color: 'var(--oa-text-2)', mt: 1 }}>
                                    {translate('marketplaceOperator.reviews.reply')}: {review.sellerReply}
                                </Typography>
                            )}
                        </Paper>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default OperatorReviewsPanel;
